// slices/messageSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import MessageService from "../services/MessageService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== HELPER: Validate ObjectId ====================
const isValidObjectId = (id) => {
  return id && /^[0-9a-fA-F]{24}$/.test(String(id));
};

// ==================== ASYNC THUNKS ====================

// -------- PUBLIC ROUTES --------

/**
 * Create a new message (public - no auth required)
 * POST /api/messages/create-message
 */
export const createMessage = createAsyncThunk(
  'message/createMessage',
  async (data, { rejectWithValue }) => {
    try {
      const response = await MessageService.createMessage(data);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ============================================================
// GET ALL MESSAGES
// ============================================================

export const getAllMessages = createAsyncThunk(
  'messages/getAllMessages',
  async (params = {}, { rejectWithValue }) => {
    try {
      console.log('📩 getAllMessages params:', params);

      const response = await MessageService.getAllMessages(params);

      console.log(
        '📩 getAllMessages service response:',
        response
      );

      // MessageService.getAllMessages() already returns
      // response.data from Axios.
      //
      // Expected backend structure:
      //
      // {
      //   success: true,
      //   message: "...",
      //   data: {
      //     items: [],
      //     pagination: {
      //       total: 0,
      //       page: 1,
      //       limit: 20,
      //       pages: 0
      //     }
      //   }
      // }

      if (!response) {
        return rejectWithValue({
          message: 'No response received from server',
          statusCode: 500
        });
      }

      if (response.success === false) {
        return rejectWithValue({
          message:
            response.error?.message ||
            response.message ||
            'Failed to retrieve messages',
          statusCode:
            response.statusCode ||
            response.error?.status ||
            500,
          data: response.data || null
        });
      }

      const data = response.data || {};

      const items = Array.isArray(data.items)
        ? data.items
        : [];

      const pagination = data.pagination || {
        total: items.length,
        page: Number(params.page) || 1,
        limit: Number(params.limit) || 20,
        pages: items.length > 0 ? 1 : 0
      };

      // Make sure every message has a convenient `id`
      // while preserving the original `_id`.
      const validMessages = items
        .filter((message) => {
          if (!message || typeof message !== 'object') {
            return false;
          }

          return Boolean(
            message._id ||
            message.id
          );
        })
        .map((message) => ({
          ...message,
          id: message.id || message._id
        }));

      console.log(
        `✅ getAllMessages: received ${validMessages.length} messages`
      );

      console.log(
        '📊 Pagination:',
        pagination
      );

      return {
        ...response,
        data: {
          ...data,
          items: validMessages,
          pagination
        }
      };

    } catch (error) {
      console.error(
        '❌ getAllMessages error:',
        error
      );

      return rejectWithValue({
        message:
          error?.message ||
          error?.error?.message ||
          'Failed to retrieve messages',
        statusCode:
          error?.statusCode ||
          error?.status ||
          500,
        data:
          error?.data ||
          null
      });
    }
  }
);

/**
 * Get message by ID with enhanced data
 * GET /api/messages/message/:id
 */
export const getMessageById = createAsyncThunk(
  'message/getMessageById',
  async (id, { rejectWithValue }) => {
    try {
      // Validate ID format before making the request
      if (!id) {
        return rejectWithValue('Message ID is required');
      }
      
      const idStr = String(id);
      if (!isValidObjectId(idStr)) {
        return rejectWithValue(`Invalid message ID format: ${idStr}`);
      }
      
      const response = await MessageService.getMessageById(idStr);
      console.log('📩 getMessageById response:', response);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update message with enhanced features
 * PUT /api/messages/update-message/:id
 */
export const updateMessage = createAsyncThunk(
  'message/updateMessage',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      // Validate ID format
      if (!id) {
        return rejectWithValue('Message ID is required');
      }
      
      const idStr = String(id);
      if (!isValidObjectId(idStr)) {
        return rejectWithValue(`Invalid message ID format: ${idStr}`);
      }
      
      const response = await MessageService.updateMessage(idStr, data);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get urgent messages
 * GET /api/messages/messages/urgent
 */
export const getUrgentMessages = createAsyncThunk(
  'message/getUrgentMessages',
  async ({ page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await MessageService.getUrgentMessages(page, limit);
      
      // Extract and validate data
      let messagesData = [];
      let paginationData = { total: 0, page: 1, limit: 20, totalPages: 0, hasNext: false, hasPrev: false };
      
      if (response?.data?.data?.items) {
        messagesData = response.data.data.items;
        paginationData = response.data.data.pagination || paginationData;
      } else if (response?.data?.data) {
        messagesData = response.data.data;
      }
      
      const validMessages = messagesData.filter(msg => msg && isValidObjectId(msg._id));
      
      return {
        ...response,
        data: {
          ...response?.data,
          data: validMessages,
          pagination: paginationData
        }
      };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get overdue messages
 * GET /api/messages/messages/overdue
 */
export const getOverdueMessages = createAsyncThunk(
  'message/getOverdueMessages',
  async ({ hours = 24, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await MessageService.getOverdueMessages(hours, page, limit);
      
      let messagesData = [];
      let paginationData = { total: 0, page: 1, limit: 20, totalPages: 0, hasNext: false, hasPrev: false };
      
      if (response?.data?.data?.items) {
        messagesData = response.data.data.items;
        paginationData = response.data.data.pagination || paginationData;
      } else if (response?.data?.data) {
        messagesData = response.data.data;
      }
      
      const validMessages = messagesData.filter(msg => msg && isValidObjectId(msg._id));
      
      return {
        ...response,
        data: {
          ...response?.data,
          data: validMessages,
          pagination: paginationData
        }
      };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get escalated messages
 * GET /api/messages/messages/escalated
 */
export const getEscalatedMessages = createAsyncThunk(
  'message/getEscalatedMessages',
  async ({ page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await MessageService.getEscalatedMessages(page, limit);
      
      let messagesData = [];
      let paginationData = { total: 0, page: 1, limit: 20, totalPages: 0, hasNext: false, hasPrev: false };
      
      if (response?.data?.data?.items) {
        messagesData = response.data.data.items;
        paginationData = response.data.data.pagination || paginationData;
      } else if (response?.data?.data) {
        messagesData = response.data.data;
      }
      
      const validMessages = messagesData.filter(msg => msg && isValidObjectId(msg._id));
      
      return {
        ...response,
        data: {
          ...response?.data,
          data: validMessages,
          pagination: paginationData
        }
      };
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get message timeline (for customer view)
 * GET /api/messages/message/:id/timeline
 */
export const getMessageTimeline = createAsyncThunk(
  'message/getMessageTimeline',
  async (id, { rejectWithValue }) => {
    try {
      if (!id || !isValidObjectId(id)) {
        return rejectWithValue('Invalid message ID format');
      }
      const response = await MessageService.getMessageTimeline(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get message analytics
 * GET /api/messages/message/analytics
 */
export const getMessageAnalytics = createAsyncThunk(
  'message/getMessageAnalytics',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await MessageService.getMessageAnalytics(startDate, endDate);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get message statistics
 * GET /api/messages/message/statistics
 */
export const getMessageStatistics = createAsyncThunk(
  'message/getMessageStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await MessageService.getMessageStatistics();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Send manual reply to message (with email notification)
 * POST /api/messages/message/:id/reply
 */
export const sendReply = createAsyncThunk(
  'message/sendReply',
  async ({ id, content, sentBy = 'admin' }, { rejectWithValue }) => {
    try {
      if (!id || !isValidObjectId(id)) {
        return rejectWithValue('Invalid message ID format');
      }
      if (!content || !content.trim()) {
        return rejectWithValue('Reply content is required');
      }
      const response = await MessageService.sendReply(id, content, sentBy);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Resend message notification
 * POST /api/messages/message/:id/resend-notification
 */
export const resendNotification = createAsyncThunk(
  'message/resendNotification',
  async (id, { rejectWithValue }) => {
    try {
      if (!id || !isValidObjectId(id)) {
        return rejectWithValue('Invalid message ID format');
      }
      const response = await MessageService.resendNotification(id);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  messages: [],
  currentMessage: null,
  selectedMessageId: null,

  loading: false,
  loadingMessage: false,

  error: null,
  submitError: null,
  updateError: null,

  submitSuccess: false,
  updateSuccess: false,
  replySuccess: false,

  urgentMessages: [],
  overdueMessages: [],
  escalatedMessages: [],

  statistics: null,
  analytics: null,

  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },

  filters: {
    status: 'all',
    priority: 'all',
    messageType: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  },

  activeTab: 'all'
};


const messageSlice = createSlice({
  name: 'message',
  initialState,
  reducers: {
    // Reset states
    resetMessageState: (state) => {
      state.messages = [];
      state.currentMessage = null;
      state.urgentMessages = [];
      state.overdueMessages = [];
      state.escalatedMessages = [];
      state.messageTimeline = [];
      state.statistics = null;
      state.analytics = null;
      state.pagination = {
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
      state.filters = {};
      state.error = null;
      state.submitError = null;
      state.updateError = null;
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.replySuccess = false;
      state.selectedMessageId = null;
    },
    
    // Clear errors
    clearMessageErrors: (state) => {
      state.error = null;
      state.submitError = null;
      state.updateError = null;
    },
    
    // Clear success states
    clearMessageSuccess: (state) => {
      state.submitSuccess = false;
      state.updateSuccess = false;
      state.replySuccess = false;
    },
    
    // Set filters
    setMessageFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    
    // Clear filters
    clearMessageFilters: (state) => {
      state.filters = {};
    },
    
    // Set pagination
    setMessagePagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    
    // Set selected message
    setSelectedMessage: (state, action) => {
      state.selectedMessageId = action.payload;
    },
    
    // Toggle replies
    toggleReplies: (state) => {
      state.showReplies = !state.showReplies;
    },
    
    // Toggle timeline
    toggleTimeline: (state) => {
      state.showTimeline = !state.showTimeline;
    },
    
    // Set active tab
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },
    
    // Set editing mode
    setEditingMessage: (state, action) => {
      state.isEditing = action.payload;
    },
    
    // Update message locally (optimistic update)
    updateLocalMessage: (state, action) => {
      const { messageId, updateData } = action.payload;
      
      // Validate ID before updating
      if (!isValidObjectId(messageId)) {
        console.warn('⚠️ Skipping local update - invalid ID:', messageId);
        return;
      }
      
      // Update in messages list
      const messageIndex = state.messages.findIndex(m => m._id === messageId);
      if (messageIndex !== -1) {
        state.messages[messageIndex] = { ...state.messages[messageIndex], ...updateData };
      }
      
      // Update current message
      if (state.currentMessage && state.currentMessage._id === messageId) {
        state.currentMessage = { ...state.currentMessage, ...updateData };
      }
      
      // Update in urgent messages
      const urgentIndex = state.urgentMessages.findIndex(m => m._id === messageId);
      if (urgentIndex !== -1) {
        state.urgentMessages[urgentIndex] = { ...state.urgentMessages[urgentIndex], ...updateData };
      }
      
      // Update in overdue messages
      const overdueIndex = state.overdueMessages.findIndex(m => m._id === messageId);
      if (overdueIndex !== -1) {
        state.overdueMessages[overdueIndex] = { ...state.overdueMessages[overdueIndex], ...updateData };
      }
      
      // Update in escalated messages
      const escalatedIndex = state.escalatedMessages.findIndex(m => m._id === messageId);
      if (escalatedIndex !== -1) {
        state.escalatedMessages[escalatedIndex] = { ...state.escalatedMessages[escalatedIndex], ...updateData };
      }
    },
    
    // Add local message
    addLocalMessage: (state, action) => {
      if (action.payload && isValidObjectId(action.payload._id)) {
        state.messages.unshift(action.payload);
      } else {
        console.warn('⚠️ Skipping add - invalid message ID:', action.payload);
      }
    },
    
    // Remove local message
    removeLocalMessage: (state, action) => {
      const messageId = action.payload;
      if (isValidObjectId(messageId)) {
        state.messages = state.messages.filter(m => m._id !== messageId);
        if (state.currentMessage && state.currentMessage._id === messageId) {
          state.currentMessage = null;
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== CREATE MESSAGE ==========
      .addCase(createMessage.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.submitSuccess = false;
      })
      .addCase(createMessage.fulfilled, (state, action) => {
        state.submitting = false;
        state.submitSuccess = true;
        state.currentMessage = action.payload.data;
        if (action.payload.data && isValidObjectId(action.payload.data._id)) {
          state.messages.unshift(action.payload.data);
        }
      })
      .addCase(createMessage.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to send message';
        state.submitSuccess = false;
      })
      
      // ========== GET ALL MESSAGES ==========
      .addCase(getAllMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
    .addCase(getAllMessages.fulfilled, (state, action) => {
  state.loading = false;
  state.error = null;

  const response = action.payload;

  // Expected:
  //
  // response.data.items
  // response.data.pagination

  const responseData =
    response?.data?.items;

  state.messages = Array.isArray(responseData)
    ? responseData
    : [];

  const pagination =
    response?.data?.pagination;

  if (pagination) {
    state.pagination = {
      total: Number(pagination.total) || 0,
      page: Number(pagination.page) || 1,
      limit: Number(pagination.limit) || 20,
      pages: Number(pagination.pages) || 0
    };
  } else {
    state.pagination = {
      total: state.messages.length,
      page: 1,
      limit: 20,
      pages: state.messages.length > 0 ? 1 : 0
    };
  }

  console.log(
    '✅ Redux messages updated:',
    state.messages.length
  );

  console.log(
    '📊 Redux pagination:',
    state.pagination
  );
})
      .addCase(getAllMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get messages';
        state.messages = [];
      })
      
      // ========== GET MESSAGE BY ID ==========
      .addCase(getMessageById.pending, (state) => {
        state.loadingMessage = true;
        state.error = null;
      })
      .addCase(getMessageById.fulfilled, (state, action) => {
        state.loadingMessage = false;
        state.currentMessage = action.payload?.data || null;
      })
      .addCase(getMessageById.rejected, (state, action) => {
        state.loadingMessage = false;
        state.error = action.payload || 'Failed to get message';
        state.currentMessage = null;
      })
      
      // ========== UPDATE MESSAGE ==========
      .addCase(updateMessage.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateMessage.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        const updatedData = action.payload?.data;
        if (updatedData && updatedData._id) {
          state.currentMessage = updatedData;
          const index = state.messages.findIndex(m => m._id === updatedData._id);
          if (index !== -1) {
            state.messages[index] = updatedData;
          }
        }
      })
      .addCase(updateMessage.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update message';
        state.updateSuccess = false;
      })
      
      // ========== GET URGENT MESSAGES ==========
      .addCase(getUrgentMessages.pending, (state) => {
        state.loadingUrgent = true;
        state.error = null;
      })
      .addCase(getUrgentMessages.fulfilled, (state, action) => {
        state.loadingUrgent = false;
        state.urgentMessages = action.payload?.data?.data || [];
        if (action.payload?.data?.pagination) {
          state.pagination = action.payload.data.pagination;
        }
      })
      .addCase(getUrgentMessages.rejected, (state, action) => {
        state.loadingUrgent = false;
        state.error = action.payload || 'Failed to get urgent messages';
        state.urgentMessages = [];
      })
      
      // ========== GET OVERDUE MESSAGES ==========
      .addCase(getOverdueMessages.pending, (state) => {
        state.loadingOverdue = true;
        state.error = null;
      })
      .addCase(getOverdueMessages.fulfilled, (state, action) => {
        state.loadingOverdue = false;
        state.overdueMessages = action.payload?.data?.data || [];
        if (action.payload?.data?.pagination) {
          state.pagination = action.payload.data.pagination;
        }
      })
      .addCase(getOverdueMessages.rejected, (state, action) => {
        state.loadingOverdue = false;
        state.error = action.payload || 'Failed to get overdue messages';
        state.overdueMessages = [];
      })
      
      // ========== GET ESCALATED MESSAGES ==========
      .addCase(getEscalatedMessages.pending, (state) => {
        state.loadingEscalated = true;
        state.error = null;
      })
      .addCase(getEscalatedMessages.fulfilled, (state, action) => {
        state.loadingEscalated = false;
        state.escalatedMessages = action.payload?.data?.data || [];
        if (action.payload?.data?.pagination) {
          state.pagination = action.payload.data.pagination;
        }
      })
      .addCase(getEscalatedMessages.rejected, (state, action) => {
        state.loadingEscalated = false;
        state.error = action.payload || 'Failed to get escalated messages';
        state.escalatedMessages = [];
      })
      
      // ========== GET MESSAGE TIMELINE ==========
      .addCase(getMessageTimeline.pending, (state) => {
        state.loadingTimeline = true;
        state.error = null;
      })
      .addCase(getMessageTimeline.fulfilled, (state, action) => {
        state.loadingTimeline = false;
        state.messageTimeline = action.payload?.data || [];
      })
      .addCase(getMessageTimeline.rejected, (state, action) => {
        state.loadingTimeline = false;
        state.error = action.payload || 'Failed to get message timeline';
        state.messageTimeline = [];
      })
      
      // ========== GET MESSAGE ANALYTICS ==========
      .addCase(getMessageAnalytics.pending, (state) => {
        state.loadingStatistics = true;
        state.error = null;
      })
      .addCase(getMessageAnalytics.fulfilled, (state, action) => {
        state.loadingStatistics = false;
        state.analytics = action.payload?.data || null;
      })
      .addCase(getMessageAnalytics.rejected, (state, action) => {
        state.loadingStatistics = false;
        state.error = action.payload || 'Failed to get analytics';
        state.analytics = null;
      })
      
      // ========== GET MESSAGE STATISTICS ==========
      .addCase(getMessageStatistics.pending, (state) => {
        state.loadingStatistics = true;
        state.error = null;
      })
      .addCase(getMessageStatistics.fulfilled, (state, action) => {
        state.loadingStatistics = false;
        state.statistics = action.payload?.data || null;
      })
      .addCase(getMessageStatistics.rejected, (state, action) => {
        state.loadingStatistics = false;
        state.error = action.payload || 'Failed to get statistics';
        state.statistics = null;
      })
      
      // ========== SEND REPLY ==========
      .addCase(sendReply.pending, (state) => {
        state.submitting = true;
        state.submitError = null;
        state.replySuccess = false;
      })
      .addCase(sendReply.fulfilled, (state, action) => {
        state.submitting = false;
        state.replySuccess = true;
        if (state.currentMessage) {
          const response = action.payload?.data;
          if (response) {
            if (!state.currentMessage.responses) {
              state.currentMessage.responses = [];
            }
            state.currentMessage.responses.push({
              content: response.response || response.content,
              sentBy: response.sentBy || 'admin',
              sentAt: response.sentAt || new Date().toISOString()
            });
            state.currentMessage.status = 'responded';
            state.currentMessage.respondedAt = response.sentAt || new Date().toISOString();
          }
        }
      })
      .addCase(sendReply.rejected, (state, action) => {
        state.submitting = false;
        state.submitError = action.payload || 'Failed to send reply';
        state.replySuccess = false;
      })
      
      // ========== RESEND NOTIFICATION ==========
      .addCase(resendNotification.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(resendNotification.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(resendNotification.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to resend notification';
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectMessageState = (state) => state.message;

// Message data selectors
export const selectMessages = createSelector(
  selectMessageState,
  (message) => message.messages
);

export const selectCurrentMessage = createSelector(
  selectMessageState,
  (message) => message.currentMessage
);

export const selectUrgentMessages = createSelector(
  selectMessageState,
  (message) => message.urgentMessages
);

export const selectOverdueMessages = createSelector(
  selectMessageState,
  (message) => message.overdueMessages
);

export const selectEscalatedMessages = createSelector(
  selectMessageState,
  (message) => message.escalatedMessages
);

export const selectMessageTimeline = createSelector(
  selectMessageState,
  (message) => message.messageTimeline
);

// Statistics selectors
export const selectMessageStatistics = createSelector(
  selectMessageState,
  (message) => message.statistics
);

export const selectMessageAnalytics = createSelector(
  selectMessageState,
  (message) => message.analytics
);

// Pagination selectors
export const selectMessagePagination = createSelector(
  selectMessageState,
  (message) => message.pagination
);

// Loading selectors
export const selectMessagesLoading = createSelector(
  selectMessageState,
  (message) => message.loading
);

export const selectMessageLoading = createSelector(
  selectMessageState,
  (message) => message.loadingMessage
);

export const selectUrgentLoading = createSelector(
  selectMessageState,
  (message) => message.loadingUrgent
);

export const selectOverdueLoading = createSelector(
  selectMessageState,
  (message) => message.loadingOverdue
);

export const selectEscalatedLoading = createSelector(
  selectMessageState,
  (message) => message.loadingEscalated
);

export const selectTimelineLoading = createSelector(
  selectMessageState,
  (message) => message.loadingTimeline
);

export const selectStatisticsLoading = createSelector(
  selectMessageState,
  (message) => message.loadingStatistics
);

export const selectMessageSubmitting = createSelector(
  selectMessageState,
  (message) => message.submitting
);

export const selectMessageUpdating = createSelector(
  selectMessageState,
  (message) => message.updating
);

// Error selectors
export const selectMessageError = createSelector(
  selectMessageState,
  (message) => message.error
);

export const selectSubmitMessageError = createSelector(
  selectMessageState,
  (message) => message.submitError
);

export const selectUpdateMessageError = createSelector(
  selectMessageState,
  (message) => message.updateError
);

// Success selectors
export const selectSubmitMessageSuccess = createSelector(
  selectMessageState,
  (message) => message.submitSuccess
);

export const selectUpdateMessageSuccess = createSelector(
  selectMessageState,
  (message) => message.updateSuccess
);

export const selectReplySuccess = createSelector(
  selectMessageState,
  (message) => message.replySuccess
);

// UI selectors
export const selectSelectedMessageId = createSelector(
  selectMessageState,
  (message) => message.selectedMessageId
);

export const selectShowReplies = createSelector(
  selectMessageState,
  (message) => message.showReplies
);

export const selectShowTimeline = createSelector(
  selectMessageState,
  (message) => message.showTimeline
);

export const selectActiveTab = createSelector(
  selectMessageState,
  (message) => message.activeTab
);

export const selectIsEditingMessage = createSelector(
  selectMessageState,
  (message) => message.isEditing
);

// Derived selectors
export const selectMessagesByStatus = createSelector(
  selectMessages,
  (messages) => {
    if (!messages || !Array.isArray(messages)) return {};
    return messages.reduce((acc, msg) => {
      const status = msg?.status || 'pending';
      if (!acc[status]) acc[status] = [];
      acc[status].push(msg);
      return acc;
    }, {});
  }
);

export const selectMessagesByPriority = createSelector(
  selectMessages,
  (messages) => {
    if (!messages || !Array.isArray(messages)) return {};
    return messages.reduce((acc, msg) => {
      const priority = msg?.priority || 'medium';
      if (!acc[priority]) acc[priority] = [];
      acc[priority].push(msg);
      return acc;
    }, {});
  }
);

export const selectPendingMessages = createSelector(
  selectMessages,
  (messages) => (messages && Array.isArray(messages) ? messages.filter(m => m?.status === 'pending') : [])
);

export const selectUnreadMessages = createSelector(
  selectMessages,
  (messages) => (messages && Array.isArray(messages) ? messages.filter(m => m?.status === 'pending' || m?.status === 'read') : [])
);

export const selectResolvedMessages = createSelector(
  selectMessages,
  (messages) => (messages && Array.isArray(messages) ? messages.filter(m => m?.status === 'resolved' || m?.status === 'closed') : [])
);

export const selectOverdueCount = createSelector(
  selectOverdueMessages,
  (messages) => (messages && Array.isArray(messages) ? messages.length : 0)
);

export const selectUrgentCount = createSelector(
  selectUrgentMessages,
  (messages) => (messages && Array.isArray(messages) ? messages.length : 0)
);

export const selectEscalatedCount = createSelector(
  selectEscalatedMessages,
  (messages) => (messages && Array.isArray(messages) ? messages.length : 0)
);

export const selectTotalMessages = createSelector(
  selectMessagePagination,
  (pagination) => pagination?.total || 0
);

// ==================== EXPORTS ====================

export const {
  resetMessageState,
  clearMessageErrors,
  clearMessageSuccess,
  setMessageFilters,
  clearMessageFilters,
  setMessagePagination,
  setSelectedMessage,
  toggleReplies,
  toggleTimeline,
  setActiveTab,
  setEditingMessage,
  updateLocalMessage,
  addLocalMessage,
  removeLocalMessage,
} = messageSlice.actions;

export default messageSlice.reducer;